import React, { useState } from 'react';
import { Supplier } from '../../graphql/entities';
import DateField from '../connectedForm/DateInput/DateInput';
import Dropdown from '../../components/connectedForm/Dropdown';
import QueryDropdown from '../../components/connectedForm/QueryDropdown';
import Label from '../connectedForm/Label/Label';
import { default as Form } from '../connectedForm';
import fieldMetaData, { IEnumMeta } from '../../graphql/schema/fieldMetaData';
import periodQuery from '../../graphql/queries/company/FinancialPeriodsQuery.graphql';
import {
  Direction,
  FilterOperation,
  IFinancialPeriod,
  IFinancialPeriodsQueryVariables,
  IGeneratePurchaseLedgerDayBookReportMutationResult,
  IGeneratePurchaseLedgerDayBookReportMutationVariables,
  IReportTemplate,
  ReportOutputType,
  ReportType,
} from '../../graphql/schema/p4types';
import GeneratePurchaseLedgerDayBookReportMutation from '../../graphql/mutations/supplier/GeneratePurchaseLedgerDayBookReportMutation.graphql';
import getClientMutationId from '../../utils/getClientMutationId';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import { useBackgroundMutation } from '../../utils/BackgroundJobs';
import { useCheckPopUpBlockerFunction } from '../../utils/CheckPopUpBlockerFunction';
import { Maybe } from 'graphql/jsutils/Maybe';
import { BasicHeader } from '../../actions/BasicHeader';
import { getDefaultOutputType } from '../../utils/getDefaultOutputType';
import { useReportTemplatesQuery } from '../../utils/hooks';
import { ModalBottomButtonContainer } from './Styles';
import { Button, SuccessButton } from '../connectedForm/Button';
import { ModalRow } from '../connectedForm/Row';
import { BasicOutputSelection } from './BasicOutputSelection';
import FluentModal from './FluentModal';

const financialPeriodCustomVariables = (): IFinancialPeriodsQueryVariables => {
  return {
    first: 100,
    orderBy: 'startDate',
    orderDirection: Direction.Desc,
    filters: [
      {
        filterOperation: FilterOperation.NotEquals,
        filterParameters: {
          field: 'supplierPostings',
          value: 'FUTURE',
        },
      },
    ],
  };
};

const PurchaseLedgerDayBookCriteriaModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const [supplier, setSupplier] = useState();
  const [template, setTemplate] = useState<Maybe<IReportTemplate>>();
  const [outputType, setOutputType] = useState<Maybe<{ value: ReportOutputType; label: string }>>();
  const [documentStartDate, setDocumentStartDate] = useState();
  const [documentFinishDate, setDocumentFinishDate] = useState();
  const [postingStartDate, setPostingStartDate] = useState();
  const [postingFinishDate, setPostingFinishDate] = useState();
  const [documentTypes, setDocumentTypes] = useState<Array<IEnumMeta<DocumentType>> | undefined>(undefined);
  const [financialPeriods, setFinancialPeriods] = useState<Array<IFinancialPeriod>>([]);

  const onChangePeriods = (values: Array<IFinancialPeriod>) => {
    const selectedValues = values?.filter((value: IFinancialPeriod) => !value.deleted) ?? [];
    setFinancialPeriods(selectedValues);
  };

  const checkPopUpBlockerFunction = useCheckPopUpBlockerFunction();
  const [generateDaybook] = useBackgroundMutation<
    IGeneratePurchaseLedgerDayBookReportMutationResult,
    IGeneratePurchaseLedgerDayBookReportMutationVariables
  >(GeneratePurchaseLedgerDayBookReportMutation, {
    onCompleteJob: (job) => {
      if (job.properties.reportFile) {
        const reportFileName: string = job.properties.reportFile;
        const newTab = checkPopUpBlockerFunction(`/api/resources/report/${reportFileName}`, '_blank');
        //An XLSX will download, rather than print.
        if (!reportFileName.endsWith('xlsx')) {
          newTab?.print();
        }
      }
      onClose();
    },
  });

  const { templates } = useReportTemplatesQuery(ReportType.DayBookReport);

  const onPrint = () => {
    generateDaybook({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          reportParameters: {
            template: normalizeVariables(template ?? null),
            outputType: outputType?.value ?? null,
          },
          dayBookReportCriteria: normalizeVariables({
            supplier: supplier,
            documentStartDate: documentStartDate,
            documentFinishDate: documentFinishDate,
            postingStartDate: postingStartDate,
            postingFinishDate: postingFinishDate,
            // the default documentType is include all sales types
            documentTypes: documentTypes
              ? documentTypes.map((documentType) => documentType.value)
              : fieldMetaData.PurchaseDocumentType.enumValues.map((documentType) => documentType.value),
            financialPeriods: financialPeriods,
          }),
        },
      },
    });
  };

  return (
    <FluentModal isOpen={isOpen} styles={{ main: { paddingBottom: '60px', minWidth: '700px', maxWidth: '1000px' } }}>
      <BasicHeader>Purchase Daybook</BasicHeader>
      <div style={{ padding: '1em' }}>
        <Dropdown
          options={templates}
          valueKey="id"
          labelKey="name"
          onChange={(newTemplate: IReportTemplate) => {
            setTemplate(newTemplate);
            setOutputType(getDefaultOutputType(newTemplate?.reportTemplateFormat));
          }}
          label="Template"
          value={template}
          inputCols={8 / 12}
          labelCols={4 / 12}
        />
        <Dropdown
          dataid="outputType"
          options={BasicOutputSelection}
          label="Output"
          onChange={setOutputType}
          value={outputType}
          valueKey="value"
          labelKey="label"
          clearable={false}
          inputCols={8 / 12}
          labelCols={4 / 12}
        />
        <QueryDropdown
          data-testid="supplier-selection"
          label="Supplier"
          {...Supplier.dropdown}
          labelCols={4 / 12}
          inputCols={8 / 12}
          value={supplier}
          onChange={setSupplier}
        />
        <Dropdown
          data-testid="purchase-ledger-daybook-document-types"
          options={fieldMetaData.PurchaseDocumentType.enumValues}
          label="Source Type"
          placeholder="Search source types"
          value={documentTypes}
          multi={true}
          valueKey="value"
          labelKey="label"
          onChange={setDocumentTypes}
          labelCols={4 / 12}
          inputCols={8 / 12}
        />
        <QueryDropdown
          data-testid="financialPeriods"
          label="Financial Period"
          dropdown={{ customVariables: financialPeriodCustomVariables }}
          query={periodQuery}
          value={financialPeriods}
          valueKey="id"
          labelKey="name"
          placeholder="Search financial periods"
          multi={true}
          clearable={true}
          onChange={onChangePeriods}
          labelCols={4 / 12}
          inputCols={8 / 12}
        />
        <Label>Document Dates</Label>
        <ModalRow>
          <Form.Fieldset cols={1 / 2} collapsed={true} style={{ paddingLeft: 0 }}>
            <DateField
              data-testid="documentStartDate"
              label="Start"
              portal={true}
              value={documentStartDate}
              onChange={setDocumentStartDate}
            />
          </Form.Fieldset>
          <Form.Fieldset cols={1 / 2} collapsed={true} style={{ paddingLeft: 0 }}>
            <DateField
              data-testid="documentFinishDate"
              label="Finish"
              portal={true}
              value={documentFinishDate}
              onChange={setDocumentFinishDate}
            />
          </Form.Fieldset>
        </ModalRow>
        <Label>Posting Dates</Label>
        <ModalRow>
          <Form.Fieldset cols={1 / 2} collapsed={true} style={{ paddingLeft: 0 }}>
            <DateField
              data-testid="postingStartDate"
              label="Start"
              portal={true}
              value={postingStartDate}
              onChange={setPostingStartDate}
            />
          </Form.Fieldset>
          <Form.Fieldset cols={1 / 2} collapsed={true} style={{ paddingLeft: 0 }}>
            <DateField
              data-testid="postingFinishDate"
              label="Finish"
              portal={true}
              value={postingFinishDate}
              onChange={setPostingFinishDate}
            />
          </Form.Fieldset>
        </ModalRow>
      </div>
      <ModalBottomButtonContainer>
        <Button data-testid="purchase-ledger-daybook-cancel" onClick={onClose}>
          Cancel
        </Button>
        <SuccessButton data-testid="purchase-ledger-daybook-print" onClick={onPrint}>
          Print
        </SuccessButton>
      </ModalBottomButtonContainer>
    </FluentModal>
  );
};

export default PurchaseLedgerDayBookCriteriaModal;
